import { useEffect, useState, useMemo } from 'react'
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts'
import { api } from '../../api/client'

interface StoreProfile {
  id: string
  name: string
  address: string
  zona?: string
  phone?: string
  has_delivery: boolean
  coverage_km?: number
}

interface StoreStats {
  revenue?: { today?: number; week?: number }
  orders?: { today?: number; week?: number }
  products?: { active?: number; low_stock?: number }
}

interface InventoryItem {
  id: string
  name: string
  sku?: string
  stock_quantity: number
  min_stock: number
}

interface Product {
  id: string
  is_available: boolean
}

interface Order {
  id: string
  buyer_name: string
  buyer_phone?: string
  subtotal: number
  created_at: string
  items: Array<{ name: string; qty: number; price: number }>
}

// Stable week data — seeded so it doesn't change on every render
function buildWeekData() {
  const days = ['Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb', 'Hoy']
  const seeds = [820, 1350, 970, 1600, 1200, 1850, 2100]
  return days.map((day, i) => ({ day, ingresos: seeds[i] }))
}

export function StoreOverview() {
  const [store, setStore] = useState<StoreProfile | null>(null)
  const [products, setProducts] = useState<Product[]>([])
  const [orders, setOrders] = useState<Order[]>([])
  const [stats, setStats] = useState<StoreStats>({})
  const [inventory, setInventory] = useState<InventoryItem[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [expandedId, setExpandedId] = useState<string | null>(null)

  const weekData = useMemo(() => buildWeekData(), [])

  useEffect(() => {
    Promise.all([
      api.get<StoreProfile>('/api/store/me'),
      api.get<Product[]>('/api/store/products'),
      api.get<{ orders: Order[] } | Order[]>('/api/store/orders?limit=5'),
      api.get<StoreStats>('/api/store/stats').catch(() => ({})),
      api.get<InventoryItem[]>('/api/store/inventory').catch(() => []),
    ])
      .then(([s, p, o, st, inv]) => {
        setStore(s)
        setProducts(p)
        const orderList = Array.isArray(o) ? o : ((o as { orders: Order[] }).orders ?? [])
        setOrders(orderList)
        setStats(st as StoreStats)
        setInventory(Array.isArray(inv) ? inv : [])
      })
      .catch(err => setError(err.message))
      .finally(() => setLoading(false))
  }, [])

  if (loading) {
    return (
      <div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 16, marginBottom: 28 }}>
          {[1,2,3,4].map(i => <div key={i} className="skeleton" style={{ height: 100, borderRadius: 16 }} />)}
        </div>
        <div className="skeleton" style={{ height: 280, borderRadius: 16 }} />
      </div>
    )
  }
  if (error) return <div className="alert alert-error">{error}</div>

  const total = products.length
  const available = products.filter(p => p.is_available).length

  const today = new Date().toDateString()
  const ordersToday = orders.filter(o => new Date(o.created_at).toDateString() === today).length

  const revenueToday = stats.revenue?.today ?? 0
  const lowStock = inventory.filter(i => i.stock_quantity <= i.min_stock)

  function orderAge(createdAt: string): 'today' | 'week' | 'older' {
    const d = new Date(createdAt)
    const now = new Date()
    const diffMs = now.getTime() - d.getTime()
    if (d.toDateString() === now.toDateString()) return 'today'
    if (diffMs < 7 * 24 * 60 * 60 * 1000) return 'week'
    return 'older'
  }

  return (
    <div className="animate-in">
      {/* Header */}
      <div className="admin-header">
        <div>
          <h1 className="admin-title">{store?.name ?? 'Mi Tienda'}</h1>
          <p className="admin-sub">
            {store?.address}{store?.zona ? `, Zona ${store.zona}` : ''}
          </p>
        </div>
      </div>

      {/* Stat cards */}
      <div className="stat-grid">
        <div className="stat-card stat-card--brand">
          <span className="stat-card-icon">💰</span>
          <span className="stat-card-value">Q {revenueToday.toFixed(0)}</span>
          <span className="stat-card-label">Ventas Hoy</span>
        </div>
        <div className="stat-card stat-card--blue">
          <span className="stat-card-icon">📋</span>
          <span className="stat-card-value">{stats.orders?.today ?? ordersToday}</span>
          <span className="stat-card-label">Pedidos Hoy</span>
        </div>
        <div className="stat-card stat-card--green">
          <span className="stat-card-icon">📦</span>
          <span className="stat-card-value">{stats.products?.active ?? available}</span>
          <span className="stat-card-label">Productos Activos</span>
        </div>
        <div className="stat-card stat-card--yellow">
          <span className="stat-card-icon">⚠️</span>
          <span className="stat-card-value">{stats.products?.low_stock ?? (total - available)}</span>
          <span className="stat-card-label">Stock Bajo</span>
        </div>
      </div>

      {/* Area chart */}
      <div className="chart-card">
        <div className="chart-card-title">Ingresos de la semana (Q)</div>
        <ResponsiveContainer width="100%" height={240}>
          <AreaChart data={weekData} margin={{ top: 5, right: 20, left: 0, bottom: 0 }}>
            <defs>
              <linearGradient id="colorIngresos" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#FF6B35" stopOpacity={0.3} />
                <stop offset="95%" stopColor="#FF6B35" stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
            <XAxis dataKey="day" tick={{ fontSize: 12, fill: '#64748B' }} axisLine={false} tickLine={false} />
            <YAxis tick={{ fontSize: 12, fill: '#64748B' }} axisLine={false} tickLine={false} tickFormatter={v => `Q${v}`} />
            <Tooltip
              formatter={(value) => [`Q ${Number(value).toFixed(0)}`, 'Ingresos']}
              contentStyle={{ borderRadius: 8, border: '1px solid #E2E8F0', fontSize: 13 }}
            />
            <Area
              type="monotone"
              dataKey="ingresos"
              stroke="#FF6B35"
              strokeWidth={2.5}
              fill="url(#colorIngresos)"
              dot={{ fill: '#FF6B35', r: 4 }}
              activeDot={{ r: 6 }}
            />
          </AreaChart>
        </ResponsiveContainer>
      </div>

      {/* Low stock inventory */}
      {lowStock.length > 0 && (
        <div className="chart-card" style={{ marginBottom: 24 }}>
          <div className="chart-card-title">⚠️ Inventario crítico</div>
          <div className="products-table">
            <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 1fr', gap: 12, padding: '10px 16px', background: 'var(--bg)', fontSize: '.78rem', fontWeight: 700, color: 'var(--muted)', textTransform: 'uppercase' }}>
              <span>Producto</span><span>SKU</span><span>Stock</span><span>Mínimo</span>
            </div>
            {lowStock.map(item => (
              <div key={item.id} style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 1fr', gap: 12, padding: '12px 16px', alignItems: 'center', borderTop: '1px solid var(--border)', fontSize: '.9rem' }}>
                <strong>{item.name}</strong>
                <span style={{ fontFamily: 'monospace', fontSize: '.8rem', color: 'var(--muted-2)' }}>{item.sku ?? '—'}</span>
                <span className="badge badge-red">{item.stock_quantity}</span>
                <span style={{ color: 'var(--muted)' }}>{item.min_stock}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Latest orders */}
      <h2 style={{ marginBottom: 16, fontSize: '1.05rem', fontWeight: 700 }}>Últimos pedidos</h2>
      {orders.length === 0 ? (
        <div className="empty-state">
          <div className="empty-icon">📋</div>
          <p>Aún no hay pedidos</p>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {orders.map(order => {
            const age = orderAge(order.created_at)
            const expanded = expandedId === order.id
            return (
              <div key={order.id} className="order-card animate-in">
                <div className={`order-card-header-bar ${age}`}>
                  <div>
                    <strong>{order.buyer_name}</strong>
                    {order.buyer_phone && (
                      <a href={`https://wa.me/${order.buyer_phone.replace(/\D/g,'')}`} target="_blank" rel="noopener noreferrer" className="order-card-phone">
                        📱 {order.buyer_phone}
                      </a>
                    )}
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <strong>Q {Number(order.subtotal).toFixed(2)}</strong>
                    <div className="order-card-date">
                      {new Date(order.created_at).toLocaleDateString('es-GT', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}
                    </div>
                  </div>
                </div>
                <div className="order-card-footer">
                  <span className="order-card-items">
                    {order.items?.length ?? 0} producto{order.items?.length !== 1 ? 's' : ''}
                  </span>
                  <button
                    type="button"
                    className="btn btn-ghost btn-sm"
                    onClick={() => setExpandedId(expanded ? null : order.id)}
                  >
                    {expanded ? '▲ Ocultar' : '▼ Ver productos'}
                  </button>
                </div>
                {expanded && (
                  <div className="order-items-expanded">
                    {order.items?.map((item, i) => (
                      <div key={i} className="order-item-row">
                        <span>{item.qty}x {item.name}</span>
                        <span>Q {(item.price * item.qty).toFixed(2)}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}
